import React, { useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Brain, Sparkles, Target, Eye, Users, Code, 
  MessageSquare, GraduationCap, ArrowRight, Shield,
  Zap, Heart, Award, BarChart3
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
            entry.target.classList.remove('opacity-0');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.scroll-reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const journey = [
    {
      year: 'April 2025',
      title: 'Foundation',
      description: 'Foundation laid by visionary engineers passionate about AI and problem-solving.'
    },
    {
      year: 'June 2025',
      title: 'Early Projects',
      description: 'Early projects in website development'
    },
    {
      year: 'Aug 2025',
      title: 'ClassIQ Launch',
      description: 'Development of AI-integrated education platforms like ClassIQ.'
    },
    {
      year: 'Oct 2025',
      title: 'Official Launch',
      description: 'ThinkVerse.AI officially launched with a mission to bring AI innovation to enterprises.'
    },
    {
      year: '2025–2026',
      title: 'Expansion',
      description: 'Expansion into Generative AI, Agentic AI, and Workflow Intelligence systems.'
    }
  ];

  const whatWeDo = [
    {
      icon: Brain,
      title: 'AI & Automation',
      description: 'Custom AI agents, automation systems, and workflow intelligence tools.'
    },
    {
      icon: Code,
      title: 'Product Engineering',
      description: 'Full-stack applications combining innovation with enterprise-grade reliability.'
    },
    {
      icon: MessageSquare,
      title: 'AI Consulting',
      description: 'Strategic guidance to help organizations adopt, scale, and optimize AI effectively.'
    },
    {
      icon: GraduationCap,
      title: 'AI Education',
      description: 'Empowering students and professionals through platforms like ClassIQ — where learning meets intelligence.'
    }
  ];

  const coreValues = [
    {
      icon: Sparkles,
      title: 'Innovation First',
      description: 'We don\'t just follow trends — we create intelligent solutions that shape the future.'
    },
    {
      icon: Shield,
      title: 'Ethics & Responsibility',
      description: 'AI built with fairness, transparency, and security at its core.'
    },
    {
      icon: Users,
      title: 'Human-Centered',
      description: 'Technology should amplify human capability, not replace it. We design with empathy.'
    },
    {
      icon: Zap,
      title: 'Speed & Agility',
      description: 'We move fast, iterate continuously, and deliver value without compromise.'
    },
    {
      icon: Heart,
      title: 'Purpose-Driven',
      description: 'Every line of code, every model, every product is guided by real-world impact.'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'We don\'t settle for good enough. We build systems that stand the test of time.'
    }
  ];

  return (
    <div className="bg-gray-50 text-gray-900 overflow-hidden">
      <Navbar />

      {/* Hero Section */}
      <section className="min-h-[50vh] flex items-center justify-center relative px-6 lg:px-12 pt-24 pb-12">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-100 to-white">
          <div className="absolute inset-0 opacity-30">
            <div
              className="w-full h-full"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233B82F6' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                backgroundSize: '60px 60px'
              }}
            ></div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/50 to-white"></div>

          {/* Floating particles */}
          <div className="absolute top-20 left-10 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-sky-500/10 rounded-full blur-3xl animate-float-delayed"></div>
        </div>

        <div className="max-w-5xl mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-light tracking-tight mb-8 leading-[1.1] text-gray-900" style={{ fontWeight: 300 }}>
            About
            <br />
            <span className="text-blue-600">ThinkVerse.AI</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-12 leading-relaxed font-light">
            ThinkVerse.AI is an innovation-first company blending Artificial Intelligence, Automation, and Analytics to create meaningful impact.
            We transform ideas into intelligent systems that empower organizations to scale smarter, move faster, and build the future responsibly.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <div className="relative inline-block group">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-sky-500 rounded-xl blur opacity-30 group-hover:opacity-60 transition-opacity duration-500"></div>
              <Button
                size="lg"
                className="relative bg-gradient-to-r from-blue-500 to-sky-500 hover:from-sky-500 hover:to-blue-500 text-white px-8 py-6 shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <span className="relative z-10">Explore Our Work</span>
                <div className="absolute inset-0 rounded-xl overflow-hidden">
                  <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
                </div>
              </Button>
            </div>
            <div className="relative inline-block group">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-sky-500 rounded-xl blur opacity-20 group-hover:opacity-40 transition-opacity duration-500"></div>
              <Button
                size="lg"
                variant="outline"
                className="relative border-2 border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white px-8 py-6 transition-all duration-300"
              >
                <span className="relative z-10">Meet the Team</span>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-32 px-6 lg:px-12 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            {/* Left Content */}
            <div className="scroll-reveal opacity-0">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 leading-tight">
                Who We
                <br />
                <span className="text-blue-500">Are</span>
              </h2>
              <div className="space-y-6 text-gray-600 text-lg leading-relaxed">
                <p>
                  At ThinkVerse.AI, we believe intelligence isn't just built — it's crafted through purpose, creativity, and precision.
                  We design intelligent ecosystems where technology and human insight merge to create next-generation AI products, platforms, and workflows.
                </p>
                <p>
                  Our multidisciplinary team of developers, designers, and data scientists collaborate to shape digital experiences and intelligent automation frameworks for enterprises and institutions worldwide.
                </p>
              </div>
            </div>

            {/* Right Visual */}
            <div className="scroll-reveal opacity-0 flex items-center justify-center">
              <div className="relative w-80 h-80">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute inset-10 border-4 border-blue-500/30 rounded-full animate-spin-slow"></div>
                <div className="absolute inset-20 border-4 border-blue-500/40 rounded-full animate-spin-reverse"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <Brain size={80} className="text-blue-500" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-32 px-6 lg:px-12 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16">
            {/* Mission */}
            <Card className="bg-white border border-gray-200 hover:border-blue-500/50 backdrop-blur-sm transition-all duration-500 hover:scale-105 hover:shadow-2xl scroll-reveal opacity-0 group relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-sky-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <CardContent className="p-8 relative">
                <div className="relative mb-6">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-sky-500 rounded-full blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500"></div>
                  <div className="relative text-blue-600 group-hover:scale-110 transition-transform duration-300">
                    <Target size={48} />
                  </div>
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6 group-hover:text-blue-600 transition-colors duration-300">Our Mission</h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  To empower every organization to harness AI responsibly, creatively, and efficiently — turning data into intelligence and problems into progress.
                </p>
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-blue-500 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
              </CardContent>
            </Card>

            {/* Vision */}
            <Card className="bg-white border border-gray-200 hover:border-blue-500/50 backdrop-blur-sm transition-all duration-500 hover:scale-105 hover:shadow-2xl scroll-reveal opacity-0 group relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-sky-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <CardContent className="p-8 relative">
                <div className="relative mb-6">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-sky-500 rounded-full blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500"></div>
                  <div className="relative text-blue-600 group-hover:scale-110 transition-transform duration-300">
                    <Eye size={48} />
                  </div>
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6 group-hover:text-blue-600 transition-colors duration-300">Our Vision</h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  To create a world where AI amplifies human capability — where intelligence is accessible, ethical, and transformative.
                </p>
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-blue-500 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Journey Section */}
      <section className="py-32 px-6 lg:px-12 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20 scroll-reveal opacity-0">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              The ThinkVerse
              <br />
              <span className="text-blue-500">Journey</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              From concept to company, ThinkVerse.AI was built on the idea that intelligence can redefine how people and businesses interact with technology.
            </p>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-blue-500 to-blue-500 hidden lg:block"></div>

            <div className="space-y-12">
              {journey.map((milestone, index) => (
                <div
                  key={index}
                  className={`flex flex-col lg:flex-row items-center gap-8 scroll-reveal opacity-0 ${
                    index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  }`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                    <Card className="bg-white border-gray-30000 hover:border-blue-500/50 transition-all duration-300">
                      <CardContent className="p-6">
                        <div className="text-4xl font-bold text-blue-500 mb-2">{milestone.year}</div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{milestone.title}</h3>
                        <p className="text-gray-600">{milestone.description}</p>
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Center dot */}
                  <div className="hidden lg:block relative">
                    <div className="w-6 h-6 rounded-full bg-blue-500 shadow-[0_0_20px_rgba(74,144,226,0.6)]"></div>
                  </div>

                  <div className="flex-1"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="py-32 px-6 lg:px-12 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20 scroll-reveal opacity-0">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              Leadership at
              <br />
              <span className="text-blue-500">ThinkVerse.AI</span>
            </h2>
            <p className="text-gray-600 text-lg mb-8">Visionaries Behind the Innovation</p>
            // <p className="text-gray-600 max-w-3xl mx-auto leading-relaxed">
            //   Together, Vijay, Hemant, and Ajay form the founding leadership of ThinkVerse.AI — a company born from curiosity, collaboration, 
            //   and a shared belief that intelligence should be designed for impact. Their diverse backgrounds — from global enterprises to grassroots 
            //   innovation — give ThinkVerse.AI its strength: a balance of data, design, and purpose.
            // </p>
            <p className="text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Together, Gaurav, Hemant form the founding leadership of ThinkVerse.AI — a company born from curiosity, collaboration, 
              and a shared belief that intelligence should be designed for impact. Their diverse backgrounds — from global enterprises to grassroots 
              innovation — give ThinkVerse.AI its strength: a balance of data, design, and purpose.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* Gaurav Sahu */}
            <Card className="bg-gradient-to-br from-white/5 to-white/[0.02] border-gray-30000 hover:border-blue-500/50 transition-all duration-300 scroll-reveal opacity-0">
              <CardContent className="p-8">
                <div className="flex items-start gap-6 mb-6">
                  <div className="relative w-24 h-24 flex-shrink-0">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/30 to-blue-600/30 rounded-full blur-xl animate-pulse"></div>
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-full">
                      <Users size={40} className="text-blue-500" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Gaurav Sahu</h3>
                    <p className="text-blue-500 text-sm mb-1">Director & Founding Team Member</p>
                    <p className="text-gray-600 text-sm">AI Generalist & Vibe Coder</p>
                  </div>
                </div>
                
                <p className="text-gray-600 mb-4 leading-relaxed text-sm">
                  Gaurav Sahu is an AI Generalist and Full-Stack Innovator leading AI-powered solutions across enterprise and education. 
                  With 5+ years of experience at Atlassian and Goldman Sachs, he blends creativity, strategy, and engineering to deliver 
                  intelligent workflows that move organizations forward.
                </p>
                
                <p className="text-gray-600 mb-4 leading-relaxed text-sm">
                  A B.Tech graduate in Computer Science Engineering from NIT Bhopal, Gaurav's professional journey reflects a rare combination 
                  of technical depth and visionary thinking. As a mentor and "vibe coder," he drives ThinkVerse.AI's vision of turning 
                  complex problems into intelligent, human-centered solutions.
                </p>

                <div className="bg-blue-500/10 border-l-4 border-blue-500 p-4 mb-4">
                  <p className="text-gray-700 italic text-sm">
                    "AI isn't just about automation. It's about amplifying human imagination."
                  </p>
                </div>

                <div className="space-y-2">
                  <p className="text-gray-600 text-xs font-semibold mb-3 uppercase tracking-wider">Highlights</p>
                  {[
                    'Director & Founding Team Member, ThinkVerse.AI',
                    'Software Developer 2, Atlassian (AI workflows across M365)',
                    'Ex-Goldman Sachs | Ex-L&T Infotech',
                    'B.Tech, NIT Bhopal | Robotics Club Founder | Rasthrapati Awardee'
                  ].map((achievement, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 flex-shrink-0"></div>
                      <p className="text-gray-600 text-xs leading-relaxed">{achievement}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* Hemant */}
            <Card className="bg-gradient-to-br from-white/5 to-white/[0.02] border-gray-30000 hover:border-blue-500/50 transition-all duration-300 scroll-reveal opacity-0">
              <CardContent className="p-8">
                <div className="flex items-start gap-6 mb-6">
                  <div className="relative w-24 h-24 flex-shrink-0">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/30 to-blue-600/30 rounded-full blur-xl animate-pulse"></div>
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-full">
                      <BarChart3 size={40} className="text-blue-500" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Hemant</h3>
                    <p className="text-blue-500 text-sm mb-1">Director & Founding Team Member</p>
                    <p className="text-gray-600 text-sm">Senior Data Engineer, PwC</p>
                  </div>
                </div>
                
                <p className="text-gray-600 mb-4 leading-relaxed text-sm">
                  Hemant is a Senior Data Engineer at PwC with 4+ years of experience in data consulting, automation, and intelligent 
                  system design. A Dayananda Sagar alumnus, he brings deep expertise in transforming complex data ecosystems into scalable, 
                  actionable intelligence.
                </p>
                
                <p className="text-gray-600 mb-4 leading-relaxed text-sm">
                  As a co-founder of ThinkVerse.AI, Hemant bridges business needs with AI capabilities — ensuring that every solution 
                  the company builds is not just technically sound, but strategically impactful. He also leads ClassIQ's data-driven 
                  learning framework, making AI education both accessible and practical for students and organizations alike.
                </p>

                <div className="bg-blue-500/10 border-l-4 border-blue-500 p-4 mb-4">
                  <p className="text-gray-700 italic text-sm">
                    "Data is the foundation of intelligence — and intelligence is the language of the future."
                  </p>
                </div>

                <div className="space-y-2">
                  <p className="text-gray-600 text-xs font-semibold mb-3 uppercase tracking-wider">Highlights</p>
                  {[
                    'Senior Data Engineer, PwC',
                    'Co-Founder, ThinkVerse.AI',
                    'Alumnus, Dayananda Sagar University',
                    'Core Lead, ClassIQ Initiative on AI Democratization'
                  ].map((achievement, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 flex-shrink-0"></div>
                      <p className="text-gray-600 text-xs leading-relaxed">{achievement}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-32 px-6 lg:px-12 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20 scroll-reveal opacity-0">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              What We
              <br />
              <span className="text-blue-500">Do</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              We build intelligent solutions that merge strategy, design, and data science to help businesses unlock AI transformation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whatWeDo.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card
                  key={index}
                  className="bg-white border-gray-30000 hover:border-blue-500/50 backdrop-blur-sm transition-all duration-300 hover:scale-105 scroll-reveal opacity-0 group"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <CardContent className="p-8 text-center">
                    <div className="mb-6 text-blue-500 inline-block group-hover:scale-110 transition-transform duration-300">
                      <Icon size={48} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4 text-gray-900">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed text-sm">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* ClassIQ Initiative Section */}
      <section className="py-32 px-6 lg:px-12 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            {/* Left Content */}
            <div className="scroll-reveal opacity-0">
              <div className="inline-block px-4 py-1 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-500 text-sm font-medium mb-6">
                Our Initiative
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                ClassIQ
                <br />
                <span className="text-blue-500">AI Education for All</span>
              </h2>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                ClassIQ is a dedicated initiative launched under ThinkVerse.AI, with a mission to bridge the knowledge gap in Artificial Intelligence. 
                We're committed to empowering students, teachers, and lifelong learners about AI, its applications, and its transformative potential.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { icon: GraduationCap, title: 'AI Learning Workshops', desc: 'Interactive and practical sessions in schools, colleges, and training institutes.' },
                  { icon: Users, title: 'Educator Enablement', desc: 'Equipping teachers with the right knowledge and tools to integrate AI in learning.' },
                  { icon: Sparkles, title: 'Student Engagement', desc: 'Introducing young minds to AI concepts, ethical considerations, and career opportunities.' }
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex items-start gap-4 p-4 bg-white rounded-lg border border-gray-30000 hover:border-blue-500/30 transition-all duration-300">
                      <Icon size={24} className="text-blue-500 flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">{item.title}</h4>
                        <p className="text-gray-600 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <a 
                href="https://classiq.thethinkverse.ai" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-500 text-gray-900 px-8 py-4 rounded-xl transition-all duration-300 shadow-[0_0_30px_rgba(74,144,226,0.4)] hover:shadow-[0_0_40px_rgba(74,144,226,0.6)] hover:scale-105 font-semibold"
              >
                Visit ClassIQ
                <ArrowRight size={20} />
              </a>
            </div>

            {/* Right Visual */}
            <div className="scroll-reveal opacity-0">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-3xl blur-3xl"></div>
                <Card className="relative bg-gradient-to-br from-white/10 to-white/5 border-blue-500/30 backdrop-blur-sm">
                  <CardContent className="p-12">
                    <div className="flex items-center justify-center mb-8">
                      <div className="relative w-32 h-32">
                        <div className="absolute inset-0 bg-blue-500/20 rounded-full animate-pulse"></div>
                        <div className="absolute inset-4 border-4 border-blue-500/40 rounded-full animate-spin-slow"></div>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <GraduationCap size={60} className="text-blue-500" />
                        </div>
                      </div>
                    </div>
                    <h3 className="text-3xl font-bold text-center mb-4 bg-gradient-to-r from-blue-500 to-[#6BA3E8] bg-clip-text text-transparent">
                      Empowering Education
                    </h3>
                    <p className="text-gray-600 text-center leading-relaxed">
                      Democratizing AI knowledge and making it accessible to students, educators, and institutions across India.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-32 px-6 lg:px-12 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20 scroll-reveal opacity-0">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Our Core
              <br />
              <span className="text-blue-500">Values</span>
            </h2>
            <p className="text-gray-600 text-lg">The principles that guide everything we build.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coreValues.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card
                  key={index}
                  className="bg-white border-gray-30000 hover:border-blue-500/50 backdrop-blur-sm transition-all duration-300 hover:scale-105 scroll-reveal opacity-0"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <CardContent className="p-8">
                    <div className="mb-6 text-blue-500">
                      <Icon size={40} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4 text-gray-900">{value.title}</h3>
                    <p className="text-gray-600 leading-relaxed text-sm">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-32 px-6 lg:px-12 bg-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 border-2 border-blue-500 rounded-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10 scroll-reveal opacity-0">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Ready to Transform Your
            <br />
            <span className="text-blue-500">Vision into Reality?</span>
          </h2>
          <p className="text-gray-600 text-lg mb-8 leading-relaxed">
            Let's build intelligent systems that shape the future. Together.
          </p>
          <div className="relative inline-block group">
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-sky-500 rounded-xl blur opacity-30 group-hover:opacity-60 transition-opacity duration-500"></div>
            <Button
              size="lg"
              className="relative bg-gradient-to-r from-blue-500 to-sky-500 hover:from-sky-500 hover:to-blue-500 text-white px-10 py-6 shadow-lg hover:shadow-2xl transition-all duration-300"
            >
              <span className="relative z-10 flex items-center">
                Start Your Journey <ArrowRight className="ml-2" size={20} />
              </span>
              <div className="absolute inset-0 rounded-xl overflow-hidden">
                <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
              </div>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
